<template>
  <div class="cdnDiv">
    <project-toolbar
      :selectMonth="true"
      :selectProject="$config.feature.project"
      :project="accountProject"
    />

    <md-content>
      <div class="md-layout">
        <div class="md-layout-item">
          <md-card>
            <md-card-header>
              <div class="md-content">Operations</div>
            </md-card-header>

            <md-card-content>
              <GChart
                type="LineChart"
                :data="operations.data"
                :options="operations.options"
              />
            </md-card-content>
          </md-card>
        </div>
      </div>

      <div class="md-layout">
        <div class="md-layout-item">
          <md-card>
            <md-card-header>
              <div class="md-content">Cache Egress</div>
            </md-card-header>

            <md-card-content>
              <div class="md-layout">
                <div class="md-layout-item md-size-75">
                  <GChart
                    v-if="egress.enabled"
                    type="AreaChart"
                    :data="egress.data"
                    :options="egress.options"
                  />
                </div>
                <div class="md-layout-item md-size-25">
                  <GChart
                    v-if="egress.enabled"
                    type="PieChart"
                    :data="egress.sumData"
                    :options="egress.sumOptions"
                  />
                </div>
              </div>
            </md-card-content>
          </md-card>
        </div>
      </div>

      <div class="md-layout">
        <div class="md-layout-item">
          <md-card>
            <md-card-header>
              <div class="md-content">Cache Fill (Intra-Region)</div>
            </md-card-header>

            <md-card-content>
              <div class="md-layout">
                <div class="md-layout-item md-size-75">
                  <GChart
                    v-if="intraFill.enabled"
                    type="AreaChart"
                    :data="intraFill.data"
                    :options="intraFill.options"
                  />
                </div>
                <div class="md-layout-item md-size-25">
                  <GChart
                    v-if="intraFill.enabled"
                    type="PieChart"
                    :data="intraFill.sumData"
                    :options="intraFill.sumOptions"
                  />
                </div>
              </div>
            </md-card-content>
          </md-card>
        </div>
      </div>

      <div v-for="interFill in interFills" :key="interFill.location">
        <div class="md-layout">
          <div class="md-layout-item">
            <md-card>
              <md-card-header>
                <div class="md-content">
                  Cache Fill (from {{ interFill.location }})
                </div>
              </md-card-header>

              <md-card-content>
                <div class="md-layout">
                  <div class="md-layout-item md-size-75">
                    <GChart
                      type="AreaChart"
                      :data="interFill.data"
                      :options="interFill.options"
                    />
                  </div>
                  <div class="md-layout-item md-size-25">
                    <GChart
                      type="PieChart"
                      :data="interFill.sumData"
                      :options="interFill.sumOptions"
                    />
                  </div>
                </div>
              </md-card-content>
            </md-card>
          </div>
        </div>
      </div>
    </md-content>
  </div>
</template>

<script>
import { GChart } from 'vue-google-charts'
import { mapState } from 'vuex'
import { iterateDateOverMonth } from '@/lib/dateutil'
import { getProject, getKindData, getBytesUnit, sumUsage } from '@/lib/aggutil'
import { distinct } from '@/lib/utils'
import api from '@/api'
import ProjectToolbar from './ProjectToolbar'

function defaultOptions(options) {
  return Object.assign(
    {},
    {
      fontSize: 12,
      tooltip: { trigger: 'focus' },
      focusTarget: 'category',
      hAxis: { title: 'Date' },
      pointSize: 4,
    },
    options
  )
}

export default {
  components: {
    GChart,
    ProjectToolbar,
  },

  data: () => ({
    data: null,
    accountProject: {
      all: false,
      list: [],
    },
  }),

  mounted() {
    this.load()
  },

  computed: {
    ...mapState({
      curAccount: (state) => state.curAccount,
      curMonth: (state) => state.curMonth,
      curProject: (state) => state.curProject,
      userInfo: (state) => state.userInfo,
    }),

    projectId() {
      return this.curProject.id
    },

    operations() {
      const kinds = ['Cache Lookup Requests', 'Cache Invalidations']
      const kindDatas = kinds.map((k) =>
        getKindData(this.data, k, this.projectId)
      )
      const data = [['Date', ...kinds]]

      iterateDateOverMonth(this.curMonth, (date, dateStr, dateOnlyStr) => {
        const values = kindDatas.map((k) => {
          const found = k.find((d) => d.date === dateStr)
          return found ? found.usage : 0
        })

        data.push([dateOnlyStr, ...values])
      })

      return {
        data,
        options: defaultOptions({
          vAxis: { title: 'Count' },
          legend: { position: 'top' },
          chartArea: { left: 100, right: 20 },
        }),
      }
    },

    egress() {
      const kindData = getKindData(this.data, 'Cache Egress', this.projectId)
      return this.setCdnByTag(kindData)
    },

    intraFill() {
      const kindData = getKindData(
        this.data,
        'Intra-Region Cache Fill',
        this.projectId
      )
      return this.setCdnByTag(kindData)
    },

    interFills() {
      const kindData = getKindData(
        this.data,
        'Inter-Region Cache Fill',
        this.projectId
      )
      const fromLocs = distinct(kindData, 'from').sort()

      return fromLocs.map((fromLoc) => {
        const fromKindData = kindData.filter((d) => d.from === fromLoc)
        return this.setCdnByTag(fromKindData, fromLoc)
      })
    },
  },

  watch: {
    curAccount() {
      this.load()
    },

    curMonth() {
      this.load()
    },
  },

  methods: {
    async load() {
      if (!this.curAccount.id) {
        return
      }
      this.data = await api.aggregate.cdnUsage(
        this.curAccount.id,
        this.curMonth
      )
      this.accountProject = getProject(
        this.data,
        this.userInfo,
        this.curAccount
      )
      if (!this.curProject.id) {
        const curProject = { id: null, name: '' }
        if (!this.accountProject.all && this.accountProject.list.length > 0) {
          curProject.id = this.accountProject.list[0].id
          curProject.name = this.accountProject.list[0].name
        } else if (this.accountProject.all) {
          curProject.id = '__total'
        }
        this.$store.commit('curProject', curProject)
      }
    },

    setCdnByTag(kindData, fromLoc) {
      const locs = distinct(kindData, 'to').sort()
      const bytesUnit = getBytesUnit(Math.max(...kindData.map((d) => d.usage)))
      const data = [['Date', ...locs]]
      const sumData = [['Location', bytesUnit.unit]]

      sumData.push(
        ...locs.map((loc) => [
          loc,
          sumUsage(kindData, (d) => d.to === loc) / bytesUnit.scale,
        ])
      )

      iterateDateOverMonth(this.curMonth, (date, dateStr, dateOnlyStr) => {
        const values = locs.map((loc) => {
          const found = kindData.find((d) => d.date === dateStr && d.to === loc)
          return found ? found.usage / bytesUnit.scale : 0
        })

        data.push([dateOnlyStr, ...values])
      })

      return {
        location: fromLoc || '',
        enabled: locs.length !== 0,
        data,
        options: defaultOptions({
          isStacked: true,
          legend: { position: 'top' },
          vAxis: { title: bytesUnit.unit },
          chartArea: { left: 100, right: 10 },
        }),
        sumData,
        sumOptions: defaultOptions({
          chartArea: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
          },
          legend: 'none',
        }),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.md-content {
  margin: 10px 8px 8px 8px;
}

.md-card {
  margin: 8px;
}
</style>
